import { useEffect, useRef, useState } from "react";
import api, { API, formatError, pollProject } from "@/lib/api";
import { toast } from "sonner";

export default function DeliveryView({ project, onUpdate }) {
  const [working, setWorking] = useState(false);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const started = useRef(false);

  const waitForDelivery = async () => {
    setWorking(true);
    setError(null);
    try {
      const data = await pollProject(project.id, (p) => !p.processing);
      onUpdate(data);
      if (data.process_error) setError(data.process_error);
    } catch (e) {
      setError("Algo no ha cargado. Vuelve a intentarlo; tu trabajo sigue aquí.");
    } finally {
      setWorking(false);
    }
  };

  const runFinalize = async () => {
    setWorking(true);
    setError(null);
    try {
      await api.post(`/projects/${project.id}/finalize`);
      await waitForDelivery();
    } catch (e) {
      setWorking(false);
      setError(formatError(e.response?.data?.detail));
    }
  };

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    if (project.status !== "delivered" && !project.processing) runFinalize();
    else if (project.processing === "finalizing") waitForDelivery();
    // eslint-disable-next-line
  }, []);

  const download = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/projects/${project.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(project.titulo_sugerido || project.title || "libro").replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Algo no ha cargado. Vuelve a intentarlo; tu trabajo sigue aquí.");
    } finally { setDownloading(false); }
  };

  if (working || (project.status !== "delivered" && !error)) {
    return (
      <div className="panel" style={{ alignItems: "center", textAlign: "center", paddingBlock: 48 }}>
        <span className="eyebrow">Maquetando</span>
        <h3 style={{ fontFamily: "var(--serif)", fontSize: 26 }}>Preparando tu libro</h3>
        <p style={{ color: "var(--muted)", maxWidth: 420 }}>Generamos la portada, los capítulos y las hojas extra.</p>
        <div className="meters" style={{ width: "100%", maxWidth: 360 }}>
          {[80, 60, 90].map((w, i) => <div className="meter" key={`m-${w}-${i}`}><span style={{ color: "var(--muted)" }}>…</span><span className="bar"><i style={{ width: `${w}%`, transition: "width .8s" }} /></span><b> </b></div>)}
        </div>
      </div>
    );
  }

  if (error && project.status !== "delivered") {
    return (
      <div className="panel" style={{ alignItems: "center", textAlign: "center", paddingBlock: 40, gap: 16 }}>
        <p style={{ color: "var(--proof)", maxWidth: 420 }}>{error}</p>
        <button className="btn btn-primary" onClick={runFinalize}>Reintentar maquetación</button>
      </div>
    );
  }

  const chapters = project.draft_chapters || [];

  return (
    <div className="panel">
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <span className="eyebrow" style={{ color: "#1F6B45" }}>Entrega</span>
        <h3 style={{ fontFamily: "var(--serif)", fontSize: 30 }}>Tu libro está listo para publicar.</h3>
      </div>

      <div className="split">
        <div className="cover-wrap">
          <div className="cover" data-testid="book-cover">
            {project.has_cover ? (
              <img data-testid="book-cover-image" src={`${API}/projects/${project.id}/cover`} alt="Portada del libro" />
            ) : (
              <>
                <span style={{ fontFamily: "var(--mono)", fontSize: 10, letterSpacing: ".2em", textTransform: "uppercase", color: "#C9D4E8" }}>{project.genre}</span>
                <span style={{ fontFamily: "var(--serif)", fontSize: 28, lineHeight: 1.05, fontStyle: "italic" }}>{project.titulo_sugerido || project.title}</span>
                <span className="beam" aria-hidden="true" />
                <span style={{ marginTop: "auto", fontFamily: "var(--serif)", fontSize: 15, letterSpacing: ".06em", textTransform: "uppercase" }}>{project.author_name}</span>
              </>
            )}
          </div>
          <small style={{ color: "var(--muted)", textAlign: "center" }}>Vista previa de portada</small>
        </div>

        <div className="stack">
          <span className="side-label">Tu libro · {project.titulo_sugerido || project.title}</span>
          <ul className="files">
            <li>
              <span className="ext">PDF</span>
              <span><b>Libro maquetado</b><small>{project.book_format_label || "formato elegido"} · {chapters.length} capítulos · portada y hojas extra</small></span>
              <span className="lock" style={{ color: "#1F6B45" }}>Disponible</span>
            </li>
          </ul>

          {chapters.length > 0 && (
            <div className="report" style={{ gap: 10 }}>
              <span className="side-label" style={{ margin: 0 }}>Índice</span>
              <ol data-testid="book-index" style={{ margin: 0, paddingLeft: 18, display: "flex", flexDirection: "column", gap: 6, maxHeight: 180, overflow: "auto" }}>
                {chapters.map((c, i) => <li key={`${i}-${(c.titulo || "cap").slice(0, 24)}`} style={{ fontFamily: "var(--serif)", fontSize: 15 }}>{c.titulo}</li>)}
              </ol>
            </div>
          )}

          <button type="button" data-testid="download-final-book-btn" className="btn btn-primary" style={{ width: "100%" }} onClick={download} disabled={downloading}>
            {downloading ? "Preparando descarga…" : "Descargar libro"}
          </button>
          <small style={{ color: "var(--muted)", textAlign: "center" }}>Enlace privado a tu libro. La publicación en tiendas llegará en la próxima versión.</small>
        </div>
      </div>
    </div>
  );
}
